import {
  ArrowRightOutlined,
  BarChartOutlined,
  BookOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CopyOutlined,
  CustomerServiceOutlined,
  DownOutlined,
  PhoneOutlined,
  ReloadOutlined,
  SignalFilled,
  ToolOutlined,
} from "@ant-design/icons";
import { Spin } from "antd";
import dayjs from "dayjs";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useGetUsimUsageQuery } from "../api/apiSlice";
import CountryListModal from "./CountryListModal/CountryListModal";
import DailyUsageList from "./DailyUsageList";
import useCOperators from "./CountryListModal/useCountryOperators";
import { UsimOrderItem } from "../schemas/UsimOrdersResponse";

const SETUP_GUIDE_URL = "https://www.sim-ply.io/installation-guide";
const BYTES_PER_GB = 1024 * 1024 * 1024;

type PanelName = "plan" | "usage" | "setup";

type UsageSummary = {
  usedGb: number;
  totalGb: number;
  remainingGb: number;
};

const SimCardIcon = () => (
  <svg viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" aria-hidden="true">
    <path d="M7 2h7l5 5v15H5V4a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" />
    <path d="M14 2v6h5M8 13h8v6H8zM11 13v6M8 16h8" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const LifeBuoyIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M9.8 9a2.4 2.4 0 1 1 3.6 2.1c-.9.5-1.4 1-1.4 2.1M12 17h.01" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const BookmarkIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" aria-hidden="true">
    <path d="M6 3h12v18l-6-4-6 4V3Z" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const Root = styled.div`
  width: 100%;
  display: grid;
  gap: 28px;
  color: #171717;
  font-family: "Bio Sans Regular", Arial, sans-serif;
  text-transform: none;

  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  button,
  a {
    font: inherit;
    text-transform: none;
  }

  button {
    cursor: pointer;
  }
`;

const Card = styled.section`
  overflow: hidden;
  border: 1px solid #efeeeb;
  border-radius: clamp(17px, 4.8vw, 24px);
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(43, 33, 22, 0.07);
`;

const HeroCard = styled(Card)`
  padding: clamp(18px, 5vw, 30px);
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(12px, 4vw, 20px);

  > div:last-child {
    min-width: 0;
  }
`;

const ProductBadge = styled.div`
  width: clamp(60px, 18vw, 82px);
  height: clamp(60px, 18vw, 82px);
  display: grid;
  place-items: center;
  flex: none;
  border-radius: 50%;
  color: #ffffff;
  background: #ff4b31;
  font-size: clamp(24px, 7vw, 35px);
`;

const ProductName = styled.h1`
  margin: 0 0 4px;
  font-family: "Diazo Cond Bold", Arial, sans-serif;
  font-size: clamp(27px, 8vw, 43px);
  line-height: 1;
  overflow-wrap: anywhere;
`;

const Muted = styled.p`
  margin: 0;
  color: #6e7480;
  font-size: clamp(14px, 4vw, 18px);
`;

const OrderLine = styled.p`
  margin: 10px 0 0;
  color: #6e7480;
  font-size: clamp(12px, 3.4vw, 15px);

  strong {
    color: #171717;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(16px, 4vw, 28px);
  margin-top: clamp(22px, 6vw, 32px);

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

const HeroColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const RightColumn = styled(HeroColumn)`
  align-items: stretch;
  padding-left: clamp(9px, 2.8vw, 20px);
  border-left: 1px solid #e7e5e1;
  text-align: left;

  @media (max-width: 650px) {
    padding-left: 0;
    padding-top: 20px;
    border-left: 0;
    border-top: 1px solid #e7e5e1;
  }
`;

const Meter = styled.div<{ $remainingPercentage: number }>`
  width: clamp(148px, 42vw, 216px);
  height: clamp(148px, 42vw, 216px);
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: ${({ $remainingPercentage }) =>
    `conic-gradient(#2da94a 0 ${$remainingPercentage}%, #e9eae8 ${$remainingPercentage}% 100%)`};

  &::before {
    width: 82%;
    height: 82%;
    grid-area: 1 / 1;
    border-radius: 50%;
    background: #ffffff;
    content: "";
  }
`;

const MeterValue = styled.div`
  z-index: 1;
  grid-area: 1 / 1;
  font-family: "Bio Sans ExtraBold", Arial, sans-serif;
  font-size: clamp(22px, 6vw, 30px);
  line-height: 1.05;

  span {
    display: block;
    margin-top: 8px;
    color: #6e7480;
    font-family: "Bio Sans Regular", Arial, sans-serif;
    font-size: clamp(13px, 3.4vw, 15px);
  }
`;

const IccidCard = styled.div`
  width: min(100%, 340px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 22px;
  padding: 14px 16px;
  border: 1px solid #e9e7e3;
  border-radius: 14px;
  background: #fffdfa;
  text-align: left;

  span {
    display: block;
    margin-bottom: 4px;
    color: #6e7480;
    font-size: 13px;
  }

  strong {
    display: block;
    overflow-wrap: anywhere;
    font-size: 15px;
  }
`;

const CopyIccid = styled.button`
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: none;
  padding: 8px 11px;
  border: 1px solid #ffb4a7;
  border-radius: 9px;
  color: #e93d27;
  background: #ffffff;
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: 14px;
`;

const ManualUsage = styled.div`
  width: 100%;
  min-height: 220px;
  display: grid;
  place-items: center;
  align-content: center;
  padding: 24px;
  border: 1px solid #e9e7e3;
  border-radius: 20px;
  background: #fafafa;
  text-align: center;

  > span {
    color: #6e7480;
    font-family: "Bio Sans SemiBold", Arial, sans-serif;
  }

  strong {
    display: block;
    margin-top: 8px;
    font-family: "Diazo Cond Bold", Arial, sans-serif;
    font-size: clamp(24px, 6vw, 32px);
    letter-spacing: -0.03em;
  }

  p {
    max-width: 290px;
    margin: 14px 0 0;
    color: #6e7480;
    line-height: 1.5;
  }
`;

const ManualIcon = styled.div`
  width: 62px;
  height: 62px;
  display: grid;
  place-items: center;
  margin-bottom: 16px;
  border-radius: 50%;
  color: #ff4b31;
  background: #fff0ed;
  font-size: 24px;
`;

const StatusItem = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: clamp(9px, 2.8vw, 14px);
  padding: 12px 0;
  border-bottom: 1px solid #e7e5e1;

  > div {
    min-width: 0;
  }
`;

const CircleIcon = styled.span<{ $success?: boolean; $warning?: boolean }>`
  width: clamp(38px, 11vw, 56px);
  height: clamp(38px, 11vw, 56px);
  display: grid;
  place-items: center;
  flex: none;
  border-radius: 50%;
  color: ${({ $success, $warning }) => ($success ? "#159b2b" : $warning ? "#ff4b31" : "#171717")};
  background: ${({ $success, $warning }) => ($success ? "#e9f8ea" : $warning ? "#fff1ed" : "#f1f1ee")};
  font-size: clamp(17px, 5vw, 25px);
`;

const StatusLabel = styled.span`
  display: block;
  margin-bottom: 3px;
  color: #6e7480;
  font-size: clamp(11px, 3.1vw, 15px);
`;

const StatusValue = styled.strong`
  max-width: 100%;
  display: inline-block;
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(13px, 3.7vw, 19px);
  line-height: 1.2;
  overflow-wrap: anywhere;
`;

const StatusChip = styled(StatusValue)<{ $active: boolean }>`
  padding: 5px 12px;
  border-radius: 7px;
  color: ${({ $active }) => ($active ? "#168f29" : "#8a5b00")};
  background: ${({ $active }) => ($active ? "#e9f8ea" : "#fff2c8")};
`;

const ActionButton = styled.button<{ $outline?: boolean }>`
  width: 100%;
  min-width: 0;
  min-height: 52px;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 2px solid #ff4b31;
  border-radius: 12px;
  color: ${({ $outline }) => ($outline ? "#171717" : "#ffffff")};
  background: ${({ $outline }) => ($outline ? "#ffffff" : "#ff4b31")};
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(13px, 3.2vw, 16px);
  line-height: 1.15;

  .button-icon {
    justify-self: start;
  }

  .button-label {
    min-width: 0;
    justify-self: center;
    text-align: center;
  }

  .arrow {
    justify-self: end;
  }
`;

const Stack = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 16px;
`;

const AccordionButton = styled.button`
  width: 100%;
  min-height: clamp(70px, 19vw, 94px);
  display: flex;
  align-items: center;
  gap: clamp(11px, 3.5vw, 18px);
  padding: clamp(12px, 3.7vw, 18px);
  border: 0;
  color: #171717;
  background: #ffffff;
  text-align: left;
`;

const AccordionIcon = styled(CircleIcon)`
  color: #171717;
  background: #fff4d7;
`;

const AccordionCopy = styled.span`
  min-width: 0;
  flex: 1;
`;

const AccordionTitle = styled.strong`
  display: block;
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(15px, 4.2vw, 21px);
  line-height: 1.2;
`;

const AccordionHint = styled.span`
  display: block;
  margin-top: 4px;
  overflow: hidden;
  color: #6e7480;
  font-size: clamp(11px, 3.1vw, 15px);
  line-height: 1.35;
  text-overflow: ellipsis;
`;

const Chevron = styled(DownOutlined)<{ $open: boolean }>`
  flex: none;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;

const AccordionBody = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.22s ease;

  > div {
    min-height: 0;
    overflow: hidden;
  }
`;

const AccordionInner = styled.div<{ $compact?: boolean }>`
  padding: ${({ $compact }) =>
    $compact ? "12px 18px 10px" : "clamp(14px, 4vw, 22px)"};
  border-top: 1px solid #e7e5e1;
`;

const DetailGrid = styled.div<{ $columns?: 2 | 3 }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns ?? 3}, minmax(0, 1fr));
  gap: clamp(7px, 2.2vw, 12px);

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

const DetailBox = styled.div`
  min-width: 0;
  padding: clamp(9px, 2.8vw, 15px);
  border: 1px solid #e7e5e1;
  border-radius: 11px;
  background: #fffdfa;

  span {
    display: block;
    margin-bottom: 5px;
    color: #6e7480;
    font-size: clamp(9px, 2.5vw, 12px);
  }

  strong {
    overflow-wrap: anywhere;
    font-size: clamp(11px, 3vw, 15px);
    line-height: 1.3;
  }
`;

const UsageList = styled.div`
  display: grid;
  gap: 10px;
`;

const UsageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: clamp(10px, 2.9vw, 14px);


  > span {
    flex: none;
    min-width: 86px;
    color: #6e7480;
  }
`;

const UsageMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex: 1;
  min-width: 0;

  strong {
    min-width: 76px;
    text-align: right;
    white-space: nowrap;
  }
`;

const UsageBar = styled.div`
  width: min(180px, 46%);
  height: 8px;
  flex: 1;
  max-width: 180px;
  overflow: hidden;
  border-radius: 999px;
  background: #eeeeee;

  span {
    display: block;
    height: 100%;
    border-radius: inherit;
  }
`;

const UsageNote = styled.p`
  margin: 2px 0 0;
  color: #6e7480;
  font-size: clamp(11px, 3.1vw, 15px);
  line-height: 1.5;
`;

const SetupSteps = styled.ol`
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: setup;

  li {
    display: grid;
    grid-template-columns: 34px 1fr;
    gap: 12px;
    align-items: start;
    line-height: 1.5;
  }

  li::before {
    counter-increment: setup;
    content: counter(setup);
    width: 30px;
    height: 30px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    color: #ffffff;
    background: #ff4b31;
    font-size: 13px;
    font-family: "Bio Sans SemiBold", Arial, sans-serif;
  }

  li > span {
    min-width: 0;
    padding-top: 2px;
  }
`;

const HelpCard = styled(Card)`
  padding: clamp(14px, 4vw, 22px);
`;

const HelpHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 12px;
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(16px, 4.5vw, 22px);
`;

const HelpGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

const HelpLink = styled.a`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: clamp(10px, 3vw, 15px);
  border: 1px solid #e7e5e1;
  border-radius: 11px;
  color: #171717;
  background: #ffffff;
  text-align: left;
  text-decoration: none;
`;

const HelpButton = styled.button`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: clamp(10px, 3vw, 15px);
  border: 1px solid #e7e5e1;
  border-radius: 11px;
  color: #171717;
  background: #ffffff;
  text-align: left;
`;

const HelpText = styled.span`
  min-width: 0;
  flex: 1;

  strong {
    display: block;
    font-size: clamp(11px, 3.2vw, 16px);
    line-height: 1.25;
  }

  span {
    display: block;
    margin-top: 3px;
    color: #6e7480;
    font-size: clamp(9px, 2.5vw, 12px);
    line-height: 1.25;
  }
`;

const FooterNote = styled.aside`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: clamp(13px, 3.8vw, 20px);
  border: 1px solid #f0d27a;
  border-radius: 14px;
  background: #fff6d9;

  > div {
    min-width: 0;

    > strong,
    > span {
      display: block;
    }

    > strong {
      font-size: clamp(12px, 3.4vw, 17px);
    }

    > span {
      margin-top: 3px;
      color: #6e7480;
      font-size: clamp(10px, 2.8vw, 14px);
    }
  }
`;

const SavedPageIcon = styled(CircleIcon)`
  width: clamp(42px, 12vw, 58px);
  height: clamp(42px, 12vw, 58px);
  color: #000000;
  background: #ffc928;
`;

const Toast = styled.div<{ $visible: boolean }>`
  position: fixed;
  z-index: 1200;
  bottom: 18px;
  left: 50%;
  padding: 11px 15px;
  border-radius: 9px;
  color: #ffffff;
  background: #191919;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: none;
  transform: translate(-50%, ${({ $visible }) => ($visible ? "0" : "70px")});
  transition: 0.2s ease;
`;

function isLiveUsageSku(sku?: string): boolean {
  const value = sku?.toLowerCase() ?? "";
  return value.startsWith("joy") || value.startsWith("elite");
}

function parseGb(value?: string): number {
  return Number(value?.match(/(\d+)\s*GB/i)?.[1] ?? 0);
}

function parseDays(value?: string): number | null {
  const match = value?.match(/(\d+)\s*D(?:ays?)?/i);
  return match ? Number(match[1]) : null;
}

function extractUssd(instruction: string): string | null {
  return instruction.match(/\*[0-9*]+#/)?.[0] ?? null;
}

function formatGb(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return value.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

function formatIccid(iccid: string): string {
  return iccid.replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();
}

function formatDate(value?: string | number | null): string {
  if (value === undefined || value === null || value === "") return "";
  const numeric = Number(value);
  const parsed = Number.isFinite(numeric) && String(value).trim().length >= 10
    ? dayjs(numeric)
    : dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "";
}

function formatPlanValidity(dataPlan?: string, sku?: string): string {
  const days = parseDays(dataPlan) ?? parseDays(sku);
  if (!days) return dataPlan || "Not available";
  return `Valid for ${days} ${days === 1 ? "day" : "days"} from activation`;
}

function formatPlanDuration(dataPlan?: string, sku?: string): string {
  const days = parseDays(dataPlan) ?? parseDays(sku);
  if (!days) return dataPlan || "Not available";
  return `${days} ${days === 1 ? "day" : "days"}`;
}

function getAllowanceGb(product: UsimOrderItem): number {
  return parseGb(product.sku) || parseGb(product.mapping?.dataPlan);
}

function getCoverageSource(product: UsimOrderItem): string {
  const details = product.mapping?.RedemptionDetails ?? {};
  return (
    details.CoverageCountries ||
    product.typeDetails?.CoverageCountries ||
    product.typeDetails?.Plan?.CoverageCountries ||
    (product.mapping?.country ? `${product.mapping.country}:` : "")
  );
}

function isActivated(product: UsimOrderItem): boolean {
  const simStatus = product.vendorStatus?.data?.simStatus;
  return simStatus === "11" || simStatus === "12" || product.status === "20";
}

function getArrivalDate(product: UsimOrderItem): string {
  const formatted = formatDate(product.activationDate);
  if (formatted) return formatted;
  return product.hasActivation ? "Not set" : "Not required";
}

function getCallsLabel(talkTime?: string): string {
  if (!talkTime || /no minutes|data only/i.test(talkTime)) return "Data only";
  return talkTime;
}

function Accordion(props: {
  idPrefix: string;
  name: PanelName;
  title: string;
  hint?: string;
  icon: ReactNode;
  open: boolean;
  onToggle: (name: PanelName) => void;
  compact?: boolean;
  children: ReactNode;
}) {
  const id = `${props.idPrefix}-${props.name}-panel`;
  return (
    <Card>
      <AccordionButton
        type="button"
        aria-expanded={props.open}
        aria-controls={id}
        onClick={() => props.onToggle(props.name)}
      >
        <AccordionIcon>{props.icon}</AccordionIcon>
        <AccordionCopy>
          <AccordionTitle>{props.title}</AccordionTitle>
          {props.hint && <AccordionHint>{props.hint}</AccordionHint>}
        </AccordionCopy>
        <Chevron $open={props.open} />
      </AccordionButton>
      <AccordionBody $open={props.open} id={id}>
        <div>
          <AccordionInner $compact={props.compact}>{props.children}</AccordionInner>
        </div>
      </AccordionBody>
    </Card>
  );
}

function UsimStatusCard(props: {
  product: UsimOrderItem;
  index: number;
  orderNo: string;
  onRefresh?: () => void;
  onToast: (message: string) => void;
}) {
  const { product, index, orderNo, onRefresh, onToast } = props;
  const isLive = isLiveUsageSku(product.sku);
  const cid = product.iccid || "";
  const orderId = product.autoRecharge?.rspOrderId || "";
  const hasCachedUsage = Boolean(product.usageData);
  const [openPanels, setOpenPanels] = useState<Record<PanelName, boolean>>({
    plan: false,
    usage: false,
    setup: false,
  });
  const [coverageOpen, setCoverageOpen] = useState(false);

  const {
    data: statusData,
    isLoading,
    isFetching,
    refetch,
  } = useGetUsimUsageQuery(
    { cid, orderId },
    { skip: !isLive || !cid || !orderId || hasCachedUsage }
  );

  const usagePayload = product.usageData ?? statusData;
  const allowance = getAllowanceGb(product);
  const instruction = product.mapping?.RedemptionDetails?.CheckDataInstruction || "";
  const ussd = extractUssd(instruction);
  const coverage = useCOperators(getCoverageSource(product));
  const coverageCount = coverage.length;
  const productName = product.mapping?.country
    ? product.mapping.country
    : `Physical SIM ${index + 1}`;
  const dataPlan = product.mapping?.dataPlan || "";
  const allowanceText = allowance ? `${formatGb(allowance)} GB` : dataPlan || "Not available";
  const durationText = formatPlanDuration(dataPlan, product.sku);
  const validityText = formatPlanValidity(dataPlan, product.sku);
  const calls = getCallsLabel(product.mapping?.talkTime);
  const activated = isActivated(product);
  const arrivalDate = getArrivalDate(product);
  const planStarts = formatDate(usagePayload?.data?.effTime);
  const planExpires = formatDate(usagePayload?.data?.expTime);
  const coverageHint = coverageCount
    ? `${coverageCount} destinations and supported operators`
    : product.mapping?.country
      ? `${product.mapping.country} coverage`
      : "Supported countries and operators";
  const planHint = [allowanceText, durationText, calls !== "Data only" ? "Calls & SMS included" : "Data only"]
    .filter(Boolean)
    .join(" • ");

  const usageSummary = useMemo<UsageSummary | null>(() => {
    if (!isLive || allowance <= 0) return null;
    const usageList = usagePayload?.data?.dataUsageList;
    let usageBytes = Number(usagePayload?.data?.totalUsage || 0);
    if (product.sku?.toLowerCase().includes("daily") && usageList?.length) {
      const latestDate = [...usageList].sort((a, b) => b.usageDate.localeCompare(a.usageDate))[0]?.usageDate;
      usageBytes = usageList
        .filter((item) => item.usageDate === latestDate)
        .reduce((sum, item) => sum + Number(item.usage || 0), 0);
    }
    const usedGb = Number.isFinite(usageBytes) ? usageBytes / BYTES_PER_GB : 0;
    return {
      usedGb,
      totalGb: allowance,
      remainingGb: Math.max(allowance - usedGb, 0),
    };
  }, [allowance, isLive, product.sku, usagePayload]);

  function togglePanel(name: PanelName) {
    setOpenPanels((current) => ({ ...current, [name]: !current[name] }));
  }

  async function copyIccid() {
    if (!cid) return;
    try {
      await navigator.clipboard.writeText(cid);
      onToast("ICCID copied");
    } catch {
      onToast("Unable to copy ICCID");
    }
  }

  function handleRefresh() {
    onRefresh?.();
    if (isLive && cid && orderId && !hasCachedUsage) {
      refetch();
    }
    onToast("Status refreshed");
  }

  const panelPrefix = `usim-${product.shopId || product.iccid || index}`;

  function openSetup() {
    setOpenPanels((current) => ({ ...current, setup: true }));
    document.getElementById(`${panelPrefix}-setup-panel`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function openSupport() {
    const frame = document.getElementById("zcw-iframe") as HTMLIFrameElement | null;
    if (!frame?.contentWindow) {
      onToast("Support chat is still loading");
      return;
    }
    frame.contentWindow.postMessage({ type: "zcw:open-chat", payload: undefined }, "*");
  }

  const remainingPercentage = usageSummary
    ? Math.min((usageSummary.remainingGb / usageSummary.totalGb) * 100, 100)
    : 0;
  const usedPercentage = usageSummary
    ? Math.min((usageSummary.usedGb / usageSummary.totalGb) * 100, 100)
    : 0;

  return (
    <div>
      <HeroCard aria-label="Physical SIM status">
        <ProductHeader>
          <ProductBadge aria-hidden="true">
            <SimCardIcon />
          </ProductBadge>
          <div>
            <ProductName>{productName}</ProductName>
            <Muted>
              Physical SIM
              {dataPlan ? ` • ${dataPlan}` : coverageCount ? ` • ${coverageCount} destinations` : ""}
            </Muted>
            {orderNo && (
              <OrderLine>
                Order number: <strong>{orderNo}</strong>
              </OrderLine>
            )}
          </div>
        </ProductHeader>

        <HeroGrid>
          {isLive ? (
            <HeroColumn aria-label="Live SIM data usage">
              {isLoading || isFetching ? (
                <Spin />
              ) : (
                <Meter $remainingPercentage={remainingPercentage}>
                  <MeterValue>
                    {usageSummary ? `${formatGb(usageSummary.remainingGb)} GB` : "—"}
                    <span>left</span>
                  </MeterValue>
                </Meter>
              )}
              <IccidCard>
                <div>
                  <span>ICCID</span>
                  <strong>{cid ? formatIccid(cid) : "Not available"}</strong>
                </div>
                <CopyIccid type="button" onClick={copyIccid} aria-label="Copy ICCID" disabled={!cid}>
                  <CopyOutlined />
                  Copy
                </CopyIccid>
              </IccidCard>
            </HeroColumn>
          ) : (
            <HeroColumn aria-label="Manual SIM data check">
              <ManualUsage>
                <ManualIcon aria-hidden="true">
                  <PhoneOutlined />
                </ManualIcon>
                <span>How to check your data</span>
                <strong>{ussd ? `Dial ${ussd}` : "Check with provider"}</strong>
                <p>
                  {instruction && !ussd
                    ? instruction
                    : "Usage is checked directly through the network operator."}
                </p>
              </ManualUsage>
              <IccidCard>
                <div>
                  <span>ICCID</span>
                  <strong>{cid ? formatIccid(cid) : "Not available"}</strong>
                </div>
                <CopyIccid type="button" onClick={copyIccid} aria-label="Copy ICCID" disabled={!cid}>
                  <CopyOutlined />
                  Copy
                </CopyIccid>
              </IccidCard>
            </HeroColumn>
          )}

          <RightColumn>
            <StatusItem>
              <CircleIcon><CalendarOutlined /></CircleIcon>
              <div>
                <StatusLabel>Arrival date</StatusLabel>
                <StatusValue>{arrivalDate}</StatusValue>
              </div>
            </StatusItem>
            <StatusItem>
              <CircleIcon $success={activated} $warning={!activated}>
                <CheckCircleFilled />
              </CircleIcon>
              <div>
                <StatusLabel>Status</StatusLabel>
                <StatusChip $active={activated}>
                  {activated ? "Activated" : "Not Activated"}
                </StatusChip>
              </div>
            </StatusItem>
            <ActionButton type="button" onClick={handleRefresh}>
              <ReloadOutlined className="button-icon" />
              <span className="button-label">Refresh status</span>
              <span className="arrow" aria-hidden="true">↻</span>
            </ActionButton>
            <ActionButton type="button" $outline onClick={openSetup}>
              <BookOutlined className="button-icon" />
              <span className="button-label">SIM setup guide</span>
              <ArrowRightOutlined className="arrow" />
            </ActionButton>
          </RightColumn>
        </HeroGrid>
      </HeroCard>

      <Stack>
        <Accordion
          idPrefix={panelPrefix}
          name="plan"
          title="SIM & Plan Details"
          hint={planHint}
          icon={<SimCardIcon />}
          open={openPanels.plan}
          onToggle={togglePanel}
        >
          {isLive ? (
            <DetailGrid>
              <DetailBox><span>Data allowance</span><strong>{allowanceText}</strong></DetailBox>
              <DetailBox><span>Plan duration</span><strong>{durationText}</strong></DetailBox>
              <DetailBox><span>Calls &amp; SMS</span><strong>{calls}</strong></DetailBox>
              {planStarts && <DetailBox><span>Plan starts</span><strong>{planStarts}</strong></DetailBox>}
              {planExpires && <DetailBox><span>Plan expires</span><strong>{planExpires}</strong></DetailBox>}
              {product.mobileNumber && (
                <DetailBox><span>Mobile number</span><strong>{product.mobileNumber}</strong></DetailBox>
              )}
            </DetailGrid>
          ) : (
            <DetailGrid $columns={2}>
              <DetailBox><span>Data allowance</span><strong>{allowanceText}</strong></DetailBox>
              <DetailBox><span>Plan validity</span><strong>{validityText}</strong></DetailBox>
              <DetailBox><span>Calls &amp; SMS</span><strong>{calls}</strong></DetailBox>
              {product.mobileNumber && (
                <DetailBox><span>Mobile number</span><strong>{product.mobileNumber}</strong></DetailBox>
              )}
            </DetailGrid>
          )}
        </Accordion>

        {isLive && usageSummary && (
          <Accordion
            idPrefix={panelPrefix}
            name="usage"
            title="Usage breakdown"
            hint="See your latest data use"
            icon={<BarChartOutlined />}
            open={openPanels.usage}
            onToggle={togglePanel}
            compact
          >
            <UsageList>
              <UsageRow>
                <strong>Data used</strong>
                <UsageMeta>
                  <UsageBar>
                    <span style={{ width: `${usedPercentage}%`, background: "#ff4b31" }} />
                  </UsageBar>
                  <strong>{formatGb(usageSummary.usedGb)} / {formatGb(usageSummary.totalGb)} GB</strong>
                </UsageMeta>
              </UsageRow>
              <UsageRow>
                <strong>Remaining</strong>
                <UsageMeta>
                  <UsageBar>
                    <span style={{ width: `${remainingPercentage}%`, background: "#2da94a" }} />
                  </UsageBar>
                  <strong>{formatGb(usageSummary.remainingGb)} GB</strong>
                </UsageMeta>
              </UsageRow>
              {usagePayload?.data?.dataUsageList?.length ? (
                <DailyUsageList
                  dataUsageList={usagePayload.data.dataUsageList.map((item) => ({
                    ...item,
                    usage: String(item.usage),
                  }))}
                  planType={product.sku?.toLowerCase().includes("daily") ? "Daily" : "Total"}
                />
              ) : null}
              <UsageNote>Usage is updated by the provider and may take a few minutes to refresh.</UsageNote>
            </UsageList>
          </Accordion>
        )}

        <Card>
          <AccordionButton type="button" aria-haspopup="dialog" onClick={() => setCoverageOpen(true)}>
            <AccordionIcon><SignalFilled /></AccordionIcon>
            <AccordionCopy>
              <AccordionTitle>Coverage &amp; network</AccordionTitle>
              <AccordionHint>{coverageHint}</AccordionHint>
            </AccordionCopy>
            <DownOutlined />
          </AccordionButton>
        </Card>

        <Accordion
          idPrefix={panelPrefix}
          name="setup"
          title="Set up your physical SIM"
          hint="Quick steps before connecting"
          icon={<PhoneOutlined />}
          open={openPanels.setup}
          onToggle={togglePanel}
        >
          <SetupSteps>
            <li><span>Insert the SIM card into <strong>SIM slot 1</strong>.</span></li>
            <li><span>Select this SIM for <strong>mobile data</strong>.</span></li>
            <li><span>Turn on <strong>data roaming</strong>.</span></li>
            <li>
              <span>
                Follow the{" "}
                <a href={SETUP_GUIDE_URL} target="_blank" rel="noreferrer">
                  product setup guide
                </a>
                {product.mapping?.RedemptionDetails?.ApnSetting
                  ? ` if APN settings are required (${product.mapping.RedemptionDetails.ApnSetting}).`
                  : " if APN settings are required."}
              </span>
            </li>
          </SetupSteps>
        </Accordion>

        <HelpCard>
          <HelpHeading>
            <CircleIcon $warning><LifeBuoyIcon /></CircleIcon>
            Need help?
          </HelpHeading>
          <HelpGrid>
            <HelpLink href={SETUP_GUIDE_URL} target="_blank" rel="noreferrer">
              <CircleIcon $warning><ToolOutlined /></CircleIcon>
              <HelpText>
                <strong>SIM troubleshooting</strong>
                <span>Fix connection or setup issues</span>
              </HelpText>
              <ArrowRightOutlined />
            </HelpLink>
            <HelpButton type="button" onClick={openSupport}>
              <CircleIcon $warning><CustomerServiceOutlined /></CircleIcon>
              <HelpText>
                <strong>Contact support</strong>
                <span>We’re here to help</span>
              </HelpText>
              <ArrowRightOutlined />
            </HelpButton>
          </HelpGrid>
        </HelpCard>
      </Stack>

      <FooterNote>
        <SavedPageIcon><BookmarkIcon /></SavedPageIcon>
        <div>
          <strong>Keep this page saved for easy access</strong>
          <span>Useful for activation updates, setup and support.</span>
        </div>
      </FooterNote>

      <CountryListModal countries={coverage} isOpen={coverageOpen} setIsOpen={setCoverageOpen} />
    </div>
  );
}

export default function UsimStatusView(props: {
  products: UsimOrderItem[];
  orderNo: string;
  onRefresh?: () => void;
}) {
  const [toast, setToast] = useState("");
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  function showToast(message: string) {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2400);
  }

  return (
    <Root>
      {props.products.map((product, index) => (
        <UsimStatusCard
          key={product.shopId || product.iccid || index}
          product={product}
          index={index}
          orderNo={props.orderNo}
          onRefresh={props.onRefresh}
          onToast={showToast}
        />
      ))}
      <Toast $visible={Boolean(toast)} role="status" aria-live="polite">{toast}</Toast>
    </Root>
  );
}
